import { useMutation } from "@apollo/client";
import type {
  UpdateAgentInput as UpdateAgentInputType,
  UpdateAgentPayload,
  AgentPatch,
} from "@casey/db-types";
import { DELETE_CUSTOM_TEMPLATE, GET_ORGANIZATION_AGENTS } from "../gql";

interface DeleteAgentInput {
  id: string;
  archivedByUserId: string;
}

interface DeleteAgentVariables {
  input: UpdateAgentInputType;
}

interface DeleteAgentResponse {
  updateAgent: UpdateAgentPayload;
}

export function useDeleteAgent() {
  const [deleteAgentMutation, { data, loading, error }] = useMutation<
    DeleteAgentResponse,
    DeleteAgentVariables
  >(DELETE_CUSTOM_TEMPLATE, {
    refetchQueries: (result) => {
      const organizationId = result.data?.updateAgent?.agent?.organizationId;
      if (organizationId) {
        return [
          { query: GET_ORGANIZATION_AGENTS, variables: { organizationId } },
        ];
      }
      return [];
    },
    // Optimistically remove from cache
    update: (cache, { data }, { variables }) => {
      if (data?.updateAgent?.agent) {
        cache.evict({
          id: cache.identify({
            __typename: "Agent",
            id: variables?.input.id,
          }),
        });
      }
    },
  });

  const deleteAgent = async (deleteData: DeleteAgentInput) => {
    try {
      const agentPatch: AgentPatch = {
        archivedAt: new Date().toISOString(),
        archivedByUserId: deleteData.archivedByUserId,
      };

      const result = await deleteAgentMutation({
        variables: {
          input: {
            id: deleteData.id,
            patch: agentPatch,
          },
        },
      });

      const archivedAgent = result.data?.updateAgent?.agent;
      return archivedAgent;
    } catch (err) {
      console.error("Error deleting agent:", err);
      throw err;
    }
  };

  return {
    deleteAgent,
    data: data?.updateAgent?.agent,
    loading,
    error,
  };
}

export type { DeleteAgentInput };

// Backwards compatibility
export { useDeleteAgent as useDeleteTemplate };
export type { DeleteAgentInput as DeleteTemplateInput };
